/**
 * Thin wrapper around the browser Notification API for "new chat message
 * while the tab is hidden/unfocused" alerts. Deliberately NOT push/FCM: these
 * only fire while the SPA is open with a live websocket. True background push
 * would need a device-token registration endpoint plus a service worker —
 * future work.
 */

const AUTO_CLOSE_MS = 8000;

/** @returns {boolean} Whether this browser supports the Notification API. */
const canUseOsNotifications = () =>
  typeof window !== "undefined" && "Notification" in window;

/**
 * Ask for notification permission if the user hasn't decided yet. Never
 * re-prompts after an explicit denial.
 * @returns {Promise<string>} "granted" | "denied" | "default" | "unsupported"
 */
export const ensurePermission = async () => {
  if (!canUseOsNotifications()) {
    return "unsupported";
  }
  if (Notification.permission === "default") {
    try {
      return await Notification.requestPermission();
    } catch (err) {
      return Notification.permission;
    }
  }
  return Notification.permission;
};

/**
 * Show an OS-level notification for a chat message. One notification per
 * room (via `tag`) so bursts collapse instead of stacking.
 * @param {Object} params
 * @param {string} params.title
 * @param {string} params.body
 * @param {string} params.roomId
 * @param {Function} params.onClick Called (after focusing the window) when
 *   the user clicks the notification.
 */
export const showMessageNotification = ({ title, body, roomId, onClick }) => {
  if (!canUseOsNotifications() || Notification.permission !== "granted") {
    return;
  }
  try {
    const notification = new Notification(title, {
      body,
      tag: `flexboard-room-${roomId}`,
    });
    notification.onclick = () => {
      try {
        window.focus();
      } catch (err) {
        // Some browsers restrict programmatic focus — navigation still runs.
      }
      onClick?.();
      notification.close();
    };
    setTimeout(() => notification.close(), AUTO_CLOSE_MS);
  } catch (err) {
    // Some platforms require a service worker for the constructor — degrade.
    console.error("OS notification failed:", err);
  }
};
